import { useState, useCallback } from 'react';
import { Patient } from '@medplum/fhirtypes';
import { notifications } from '@mantine/notifications';
import { IconCircleCheck, IconAlertCircle } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useHandlePatientSubmit } from './useHandlePatientSubmit';
import { useHandleRelatedPersonSubmit } from './useHandleRelatedPersonSubmit';
import { RELATION_TYPES, RelationType } from './usePatientFormInitialization';

interface UsePatientSubmitParams {
  isNewPatient: boolean;
  onPatientSaved: (patient: Patient) => void;
}

export const usePatientSubmit = ({
  isNewPatient,
  onPatientSaved,
}: UsePatientSubmitParams) => {
  const { t } = useTranslation(['patient-onboarding']);
  const { handlePatientSubmit } = useHandlePatientSubmit();
  const { handleRelatedPersonSubmit } = useHandleRelatedPersonSubmit();
  const [loading, setLoading] = useState(false);

  const onSubmit = useCallback(
    async (
      values: Record<string, any>,
      patient: Patient | null,
      relatedPersons: Partial<Record<RelationType, any>>
    ): Promise<void> => {
      setLoading(true);
      try {
        const submittedPatient = await handlePatientSubmit(values, patient);

        await Promise.all(
          RELATION_TYPES.map(async (type) =>
            handleRelatedPersonSubmit(
              relatedPersons[type] ?? null,
              values,
              type,
              submittedPatient
            )
          )
        );

        notifications.show({
          color: 'green',
          icon: <IconCircleCheck />,
          title: 'Success',
          message: isNewPatient
            ? 'Patient created successfully'
            : 'Patient information updated',
        });

        onPatientSaved(submittedPatient);
      } catch (error) {
        console.error('Error saving patient:', error);

        // Don't show generic error for AMKA conflicts - they already show specific notifications
        const isAmkaConflictError =
          error instanceof Error &&
          (error.message === 'AMKA_ALREADY_EXISTS' ||
            error.message === 'AMKA_BELONGS_TO_ANOTHER_PATIENT');

        if (!isAmkaConflictError) {
          notifications.show({
            color: 'red',
            icon: <IconAlertCircle />,
            title: 'Error',
            message: t('patient.errors.saveFailed'),
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [
      handlePatientSubmit,
      handleRelatedPersonSubmit,
      isNewPatient,
      onPatientSaved,
      t,
    ]
  );

  return {
    onSubmit,
    loading,
  };
};
